import { useState, useRef, useEffect } from 'react';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import LoadingButton from '@mui/lab/LoadingButton';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { useFormik } from 'formik';
import { useAppDispatch, useAppSelector } from '../../store';
import { updateUserProfile } from '../../store/users/api';
import { setUserProfile } from '../../store/auth/authSlice';
import UserUpdateSchema from '../../validation/userUpdateSchema';
import { Box, Avatar, IconButton, Tooltip, CircularProgress, Typography } from '@mui/material';
import { UploadAssetsApiCall } from '../../utils/uploadAssetsApiCall';
import { getAssetURl } from '../../utils/url';
import { getItem } from '../../utils/localStorage';
import { toast } from 'react-toastify';
import { SuperUserRole } from '../../enum';

const Profile: React.FC = () => {
  const dispatch = useAppDispatch();
  const userProfile = useAppSelector((state) => state.auth.user);
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const organizationId = getItem('oinfo')?.id || null;
  const isCompanyAdmin = userProfile?.userRole === SuperUserRole.company;
  
  const [uploading, setUploading] = useState(false);
  const [profileImageId, setProfileImageId] = useState<string | null>(
    userProfile?.userProfileImage?.id || null
  );
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(
    userProfile?.userProfileImage?.key 
      ? getAssetURl(userProfile.userProfileImage.key)
      : null
  );

  // Update preview when user profile changes
  useEffect(() => {
    if (userProfile?.userProfileImage?.key) {
      setProfileImagePreview(getAssetURl(userProfile.userProfileImage.key));
      setProfileImageId(userProfile.userProfileImage.id);
    }
  }, [userProfile?.userProfileImage?.key, userProfile?.userProfileImage?.id]);

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Upload image
      const uploadType = 'userprofile';
      const uploadResult = await UploadAssetsApiCall(
        file,
        organizationId || '',
        accessToken,
        uploadType
      );

      if (uploadResult?.id) {
        setProfileImageId(uploadResult.id);
        toast.success('Profile image uploaded successfully');
      } else {
        toast.error('Failed to upload profile image');
        setProfileImagePreview(
          userProfile?.userProfileImage?.key 
            ? getAssetURl(userProfile.userProfileImage.key)
            : null
        );
      }
    } catch (error: any) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload profile image');
      setProfileImagePreview(
        userProfile?.userProfileImage?.key 
          ? getAssetURl(userProfile.userProfileImage.key)
          : null
      );
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const { handleSubmit, values, handleChange, isSubmitting, errors, touched } =
    useFormik({
      initialValues: {
        id: userProfile.id,
        name: userProfile.name || '',
        language: userProfile.language || 'en',
        description: (userProfile as any).description || '',
        attachmentId: userProfile?.userProfileImage?.id || null,
      },
      validationSchema: UserUpdateSchema,
      enableReinitialize: true,
      onSubmit: async (values, { setSubmitting }) => {
        try {
          // Include the uploaded profile image ID if available
          const submitValues = {
            id: values.id,
            name: values.name.trim(),
            language: values.language,
            description: values.description?.trim() || null,
            attachmentId: profileImageId || values.attachmentId || null,
          };
          const response = await dispatch(updateUserProfile(submitValues)).unwrap();
          dispatch(setUserProfile({ user: response }));
          toast.success('Profile updated successfully');
        } catch (error: any) {
          console.error('Error updating profile:', error);
          // Error toast is handled by the API thunk
        } finally {
          setSubmitting(false);
        }
      },
    });

  return (
    <Grid
      item
      xs={12}
      md={6}
    >
      <Card sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          {/* Profile Picture Upload Section */}
          <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Box sx={{ position: 'relative' }}>
              <Tooltip title="Click to change profile picture">
                <IconButton
                  onClick={handleImageClick}
                  disabled={uploading}
                  sx={{
                    p: 0,
                    '&:hover': {
                      opacity: 0.8,
                    },
                  }}
                >
                  <Avatar
                    src={profileImagePreview || undefined}
                    alt={userProfile.name}
                    sx={{
                      width: 120,
                      height: 120,
                      border: '3px solid',
                      borderColor: 'primary.main',
                      cursor: 'pointer',
                      fontSize: '3rem',
                    }}
                  >
                    {!profileImagePreview && userProfile.name?.charAt(0)?.toUpperCase()}
                  </Avatar>
                </IconButton>
              </Tooltip>
              {uploading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    borderRadius: '50%',
                  }}
                >
                  <CircularProgress size={40} sx={{ color: 'white' }} />
                </Box>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: 'none' }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              Click on your profile picture to upload a new one
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              columnGap: 2,
              rowGap: 3,
              gridTemplateColumns: {
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
              },
            }}
          >
            <TextField
              id='name'
              name='name'
              label='Name'
              fullWidth
              required
              disabled={isCompanyAdmin}
              onChange={handleChange}
              value={values.name}
              error={Boolean(touched.name && errors.name)}
              helperText={Boolean(errors.name && touched.name) && errors.name}
            />
            <TextField
              id='language'
              name='language'
              label='Language'
              fullWidth
              select
              onChange={handleChange}
              value={values.language}
              error={Boolean(touched.language && errors.language)}
              helperText={Boolean(errors.language && touched.language) && errors.language}
            >
              <MenuItem value='en'>English</MenuItem>
              <MenuItem value='ar'>Arabic</MenuItem>
            </TextField>
            <TextField
              id='description'
              name='description'
              label='Description'
              fullWidth
              multiline
              rows={4}
              onChange={handleChange}
              value={values.description}
              error={Boolean(touched.description && errors.description)}
              helperText={Boolean(errors.description && touched.description) ? String(errors.description) : ''}
              sx={{
                gridColumn: { xs: '1 / -1', sm: '1 / -1' },
              }}
            />
          </Box>

          <Stack
            alignItems='flex-end'
            sx={{ mt: 1.5 }}
          >
            <LoadingButton
              type='submit'
              variant='contained'
              loading={isSubmitting}
            >
              Update Profile
            </LoadingButton>
          </Stack>
        </form>
      </Card>
    </Grid>
  );
};

export default Profile;
